<template>
  <div class="AchievementBadge">
    <!-- Static Badge Display -->
    <div v-if="!showAnimation" class="AchievementBadge__static">
      <div class="AchievementBadge__badge">
        <img 
          :src="badgeImage" 
          :alt="badgeTitle"
          class="AchievementBadge__image"
        />
      </div>
      <div v-if="showTitle" class="AchievementBadge__title">
        {{ badgeTitle }}
      </div>
      <div v-if="showDescription" class="AchievementBadge__description">
        {{ badgeDescription }}
      </div>
    </div>

    <!-- Animated Award Sequence -->
    <div v-else class="AchievementBadge__animation-container">
      <!-- Background Overlay -->
      <div 
        class="AchievementBadge__overlay"
        :class="{ 'AchievementBadge__overlay--visible': animationStarted }"
      ></div>

      <!-- Award Sequence Content -->
      <div 
        class="AchievementBadge__animation-content"
        :class="{ 'AchievementBadge__animation-content--visible': animationStarted }"
      >
        <!-- Sparkle Effects -->
        <div class="AchievementBadge__sparkles">
          <div 
            v-for="i in 12" 
            :key="'sparkle-' + i"
            class="AchievementBadge__sparkle"
            :style="getSparkleStyle(i)"
          ></div>
        </div>

        <!-- Central Badge -->
        <div 
          class="AchievementBadge__badge-container"
          :class="{ 
            'AchievementBadge__badge-container--reveal': badgeRevealed,
            'AchievementBadge__badge-container--rotate': badgeRotating 
          }"
        >
          <div class="AchievementBadge__badge AchievementBadge__badge--animated">
            <img 
              :src="badgeImage" 
              :alt="badgeTitle"
              class="AchievementBadge__image AchievementBadge__image--animated"
            />
          </div>
        </div>

        <!-- Text Content -->
        <div 
          class="AchievementBadge__text-content"
          :class="{ 'AchievementBadge__text-content--visible': textVisible }"
        >
          <h3 class="AchievementBadge__animation-title">{{ badgeTitle }}</h3>
          <p class="AchievementBadge__animation-description">{{ badgeDescription }}</p>
        </div>

        <!-- Continue Button -->
        <v-btn
          v-if="continueButtonVisible"
          @click="completeAnimation"
          class="AchievementBadge__continue-btn"
          :class="{ 'AchievementBadge__continue-btn--visible': continueButtonVisible }"
          color="orange"
          dark
        >
          <v-icon left>mdi-check</v-icon>
          Awesome!
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AchievementBadge',
  props: {
    badgeTitle: {
      type: String,
      default: 'Onboarding Complete!'
    },
    badgeDescription: {
      type: String,
      default: 'You\'ve successfully completed all onboarding tasks and unlocked the full potential of Forge.'
    },
    badgeImage: {
      type: String,
      default: require('@/assets/forge-badge.png')
    },
    showAnimation: {
      type: Boolean,
      default: false
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showDescription: {
      type: Boolean,
      default: true
    },
    autoStart: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      animationStarted: false,
      badgeRevealed: false,
      badgeRotating: false,
      textVisible: false,
      continueButtonVisible: false,
      sparklePositions: []
    }
  },
  mounted() {
    if (this.showAnimation && this.autoStart) {
      this.startAnimation()
    }
    this.generateSparklePositions()
  },
  methods: {
    generateSparklePositions() {
      // Generate random positions for sparkles around the badge
      this.sparklePositions = []
      for (let i = 0; i < 12; i++) {
        const angle = (i * 30) + Math.random() * 15 - 7.5 // Distribute around circle with some randomness
        const distance = 120 + Math.random() * 40 // Distance from center
        const x = Math.cos(angle * Math.PI / 180) * distance
        const y = Math.sin(angle * Math.PI / 180) * distance
        
        this.sparklePositions.push({
          x: x + 'px',
          y: y + 'px',
          delay: Math.random() * 2000 + 'ms',
          duration: (Math.random() * 1000 + 1500) + 'ms',
          size: (Math.random() * 8 + 4) + 'px'
        })
      }
    },
    
    getSparkleStyle(index) {
      const position = this.sparklePositions[index - 1] || { x: '0px', y: '0px', delay: '0ms', duration: '1500ms', size: '6px' }
      return {
        left: `calc(50% + ${position.x})`,
        top: `calc(50% + ${position.y})`,
        animationDelay: position.delay,
        animationDuration: position.duration,
        width: position.size,
        height: position.size
      }
    },
    
    async startAnimation() {
      // Step 1: Show overlay and start sparkles
      this.animationStarted = true
      
      // Step 2: Reveal badge after a short delay
      setTimeout(() => {
        this.badgeRevealed = true
      }, 300)
      
      // Step 3: Start badge rotation
      setTimeout(() => {
        this.badgeRotating = true
      }, 800)
      
      // Step 4: Show text content
      setTimeout(() => {
        this.textVisible = true
      }, 2000)
      
      // Step 5: Show continue button
      setTimeout(() => {
        this.continueButtonVisible = true
      }, 2800)
    },
    
    completeAnimation() {
      this.$emit('animation-complete')
      // Optionally hide the animation
      if (this.autoStart) {
        this.hideAnimation()
      }
    },
    
    hideAnimation() {
      // Fade out animation
      this.continueButtonVisible = false
      this.textVisible = false
      
      setTimeout(() => {
        this.badgeRotating = false
        this.badgeRevealed = false
      }, 300)
      
      setTimeout(() => {
        this.animationStarted = false
        this.$emit('animation-hidden')
      }, 600)
    },
    
    // Public method to restart animation
    restartAnimation() {
      this.resetAnimation()
      this.$nextTick(() => {
        this.startAnimation()
      })
    },
    
    resetAnimation() {
      this.animationStarted = false
      this.badgeRevealed = false
      this.badgeRotating = false
      this.textVisible = false
      this.continueButtonVisible = false
    }
  }
}
</script>

<style lang="scss">
$style: AchievementBadge;

.#{$style} {
  position: relative;
  
  // Static Badge Styles
  &__static {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  &__badge {
    position: relative;
    margin-bottom: 12px;
    
    &--animated {
      filter: drop-shadow(0 8px 16px rgba(245, 158, 11, 0.3));
    }
  }
  
  &__image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    
    &--animated {
      width: 120px;
      height: 120px;
    }
  }
  
  &__title {
    font-size: $H16;
    font-weight: 600;
    color: $white;
    margin-bottom: 4px;
  }
  
  &__description {
    font-size: $H12;
    color: $N2;
    line-height: 1.4;
    max-width: 250px;
  }
  
  // Animation Styles
  &__animation-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10000;
    pointer-events: none;
    
    &.active {
      pointer-events: all;
    }
  }
  
  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.85);
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
    
    &--visible {
      opacity: 1;
      pointer-events: all;
    }
  }
  
  &__animation-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
    
    &--visible {
      opacity: 1;
      pointer-events: all;
    }
  }
  
  // Sparkles
  &__sparkles {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }
  
  &__sparkle {
    position: absolute;
    width: 6px;
    height: 6px;
    background: linear-gradient(45deg, $Y1, lighten($Y1, 20%));
    border-radius: 50%;
    opacity: 0;
    animation: sparkle-twinkle 1.5s ease-in-out infinite;
    pointer-events: none;
    
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 1px;
      height: 150%;
      background: linear-gradient(to bottom, transparent, $Y1, transparent);
      transform: translate(-50%, -50%);
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 150%;
      height: 1px;
      background: linear-gradient(to right, transparent, $Y1, transparent);
      transform: translate(-50%, -50%);
    }
  }
  
  // Badge Container Animation
  &__badge-container {
    position: relative;
    margin-bottom: 32px;
    opacity: 0;
    transform: scale(0.3) rotateY(-180deg);
    transition: all 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    
    &--reveal {
      opacity: 1;
      transform: scale(1) rotateY(0deg);
    }
    
    &--rotate {
      animation: badge-float 3s ease-in-out infinite;
    }
  }
  
  // Text Content
  &__text-content {
    margin-bottom: 32px;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s ease;
    
    &--visible {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &__animation-title {
    font-size: $H24;
    font-weight: 700;
    color: $white;
    margin: 0 0 12px 0;
    background: linear-gradient(45deg, $Y1, lighten($Y1, 20%));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  &__animation-description {
    font-size: $H14;
    color: $N2;
    line-height: 1.5;
    max-width: 400px;
    margin: 0;
  }
  
  // Continue Button
  &__continue-btn {
    opacity: 0;
    transform: translateY(10px);
    transition: all 0.4s ease;
    pointer-events: all;
    
    &--visible {
      opacity: 1;
      transform: translateY(0);
    }
    
    &.v-btn {
      text-transform: none !important;
      font-weight: 600;
      font-size: $H14 !important;
      padding: 12px 24px !important;
      height: auto !important;
      border-radius: 8px !important;
      
      &.theme--dark.v-btn.orange {
        background: linear-gradient(45deg, $Y1, lighten($Y1, 10%)) !important;
        box-shadow: 0 4px 12px rgba($Y1, 0.3) !important;
        
        &:hover {
          box-shadow: 0 6px 20px rgba($Y1, 0.4) !important;
          transform: translateY(-2px);
        }
      }
    }
  }
}

// Keyframe Animations
@keyframes sparkle-twinkle {
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes badge-float {
  0%, 100% {
    transform: scale(1) rotateY(0deg) translateY(0px);
  }
  25% {
    transform: scale(1.05) rotateY(5deg) translateY(-5px);
  }
  50% {
    transform: scale(1) rotateY(0deg) translateY(-8px);
  }
  75% {
    transform: scale(1.05) rotateY(-5deg) translateY(-5px);
  }
}

// Responsive adjustments
@media screen and (max-width: 600px) {
  .#{$style} {
    &__animation-title {
      font-size: $H20;
    }
    
    &__animation-description {
      font-size: $H12;
      max-width: 320px;
    }
    
    &__image--animated {
      width: 100px;
      height: 100px;
    }
  }
}
</style>
